import { GAEventWithCallback } from '@noths/polaris-client-google-analytics';
import { _Omit } from '@noths/polaris-dev-ts-recipes';
import type {
  EcommerceAddEvent,
  EcommerceClickEvent,
  EcommerceImpressionsEvent,
  EcommerceRemoveEvent,
  NullEcommerceEvent,
  PlacementProductFieldObject,
  PlacementProductListFieldObject,
} from '@noths/polaris-dev-ts-types';

import { Placement } from 'src/constants/recommendations';
import type { ProductEvent, ProductEventWithEcommerce } from 'src/types';

export const CROSS_SELL_MODAL_VISIBILITY_NAME = 'crossSellModal_visible';

type CrossSellEventLabel = `${string} : ${Placement.CrossSell}`;

type EcommerceActionFieldList = `product - rich relevance - ${Placement.CrossSell} - ${string}`;

type EcommerceProducts = PlacementProductListFieldObject[] | PlacementProductFieldObject[];
type AddCardEcommerceEvent = EcommerceAddEvent<EcommerceActionFieldList, EcommerceProducts>;
type ClickCardEcommerceEvent = EcommerceClickEvent<EcommerceActionFieldList, EcommerceProducts>;
type RemoveCardEcommerceEvent = EcommerceRemoveEvent<EcommerceActionFieldList, EcommerceProducts>;

type CrossSellAlgonomyResponse<TResponseType extends 'True' | 'False'> = ProductEvent<
  'custom_event',
  'Rich Relevance',
  TResponseType,
  'API Response'
>;

type CrossSellBasketAction<T extends 'add' | 'remove'> = ProductEventWithEcommerce<
  T extends 'add' ? 'add_to_cart' : 'remove_from_cart',
  T extends 'add' ? 'Add to basket' : 'Remove from basket',
  CrossSellEventLabel,
  T extends 'add' ? AddCardEcommerceEvent : RemoveCardEcommerceEvent,
  'Rich Relevance'
> & {
  algonomy_tracking_URL?: string;
};

export type CrossSellProductImpressions = ProductEventWithEcommerce<
  'product_impressions',
  'Product Impressions',
  CrossSellEventLabel,
  EcommerceImpressionsEvent<_Omit<PlacementProductListFieldObject, 'quantity'>[]>,
  'Rich Relevance'
>;

export type CrossSellEcommerceEvent = NullEcommerceEvent;

export type CrossSellProductClick = ProductEventWithEcommerce<
  'product_clicks',
  'Product Click',
  `${number} : ${CrossSellEventLabel}`,
  ClickCardEcommerceEvent,
  'Rich Relevance'
> & {
  algonomy_tracking_URL: string;
};

export type CrossSellAddCardToBasket = CrossSellBasketAction<'add'>;
export type CrossSellRemoveCardFromBasket = CrossSellBasketAction<'remove'>;

export type CrossSellContinueToCartEvent = ProductEvent<
  'custom_event',
  'Basket click',
  CrossSellEventLabel,
  'Rich Relevance'
> &
  GAEventWithCallback;

export type CrossSellCloseModalEvent = ProductEvent<
  'custom_event',
  'Close modal click',
  CrossSellEventLabel,
  'Rich Relevance'
>;

export type CrossSellAlgonomyResponseTrue = CrossSellAlgonomyResponse<'True'>;
export type CrossSellAlgonomyResponseFalse = CrossSellAlgonomyResponse<'False'>;

export type CrossSellCarouselSwipe = ProductEvent<
  'custom_event',
  'Swipe next' | 'Swipe previous',
  CrossSellEventLabel,
  'Rich Relevance'
>;
